import { useEffect, useState } from "react";

import { isManagerApp } from "shared/utils/auth";
import {
  CUSTOMERIO_TRACK_SCRIPT_SRC,
  CUSTOMERIO_TRACKER_SCRIPT_ID,
  isCustomerioSnippetScriptReady,
} from "shared/utils/customerio";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import { useCustomerioIdentify } from "./useCustomerioIdentify";

const SCRIPT_ID = CUSTOMERIO_TRACKER_SCRIPT_ID;

/** Survives React Strict Mode remounts when track.js already loaded. */
let snippetReadyPersisted = false;

const useCustomerIOSnippet = (isEnabled: boolean) => {
  const {
    REACT_APP_ENVIRONMENT: ENV,
    REACT_APP_CUSTOMERIO_SANDBOX_KEY: SANDBOX_SITE_ID,
    REACT_APP_CUSTOMERIO_PROD_KEY: PROD_SITE_ID,
  } = process.env;

  const isProd = ENV === "production";
  const siteId = isProd ? PROD_SITE_ID : SANDBOX_SITE_ID;
  const isDevLike = !isProd;

  const [snippetReady, setSnippetReady] = useState(
    () => snippetReadyPersisted || isCustomerioSnippetScriptReady()
  );

  useEffect(() => {
    if (!isManagerApp()) return;

    if (!isEnabled) {
      setSnippetReady(false);
      return;
    }

    if (!siteId) {
      if (isDevLike) {
        console.warn(
          `[CustomerIO] REACT_APP_CUSTOMERIO_${
            isProd ? "PROD" : "SANDBOX"
          }_KEY is not set for environment "${ENV}". Snippet will not load.`
        );
      }
      setSnippetReady(false);
      return;
    }

    let pollId: number | undefined;

    const markSnippetReady = () => {
      if (pollId) {
        window.clearInterval(pollId);
        pollId = undefined;
      }
      snippetReadyPersisted = true;
      document.getElementById(SCRIPT_ID)?.setAttribute("data-cio-loaded", "true");
      setSnippetReady(true);
    };

    const markSnippetFailed = () => {
      if (pollId) {
        window.clearInterval(pollId);
        pollId = undefined;
      }
      document.getElementById(SCRIPT_ID)?.setAttribute("data-cio-error", "true");
      snippetReadyPersisted = false;
      setSnippetReady(false);
    };

    const tryMarkSnippetReady = () => {
      if (snippetReadyPersisted || isCustomerioSnippetScriptReady()) {
        markSnippetReady();
        return true;
      }
      return false;
    };

    if (tryMarkSnippetReady()) {
      return undefined;
    }

    const startSnippetReadyPoll = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        tryMarkSnippetReady();
      }, 200);
      window.setTimeout(() => tryMarkSnippetReady(), 0);
    };

    let existingScript = document.getElementById(
      SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existingScript?.getAttribute("data-cio-error") === "true") {
      existingScript.remove();
      existingScript = null;
    }

    if (existingScript) {
      if (!tryMarkSnippetReady()) {
        existingScript.addEventListener("load", markSnippetReady, {
          once: true,
        });
        existingScript.addEventListener("error", markSnippetFailed, {
          once: true,
        });
        startSnippetReadyPoll();
      }
      return () => {
        if (pollId) window.clearInterval(pollId);
      };
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const w = window as any;
    w._cio = w._cio || [];
    const stub = (f: string) =>
      function (...args: unknown[]) {
        w._cio.push([f, ...args]);
      };
    ["load", "identify", "sidentify", "track", "page", "on", "off", "reset"].forEach(
      (m) => {
        w._cio[m] = stub(m);
      }
    );
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.setAttribute("data-site-id", siteId);
    script.setAttribute("data-use-array-params", "true");
    script.setAttribute("data-use-in-app", "true");
    script.src = CUSTOMERIO_TRACK_SCRIPT_SRC;
    script.addEventListener("load", markSnippetReady, { once: true });
    script.addEventListener("error", markSnippetFailed, { once: true });

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    startSnippetReadyPoll();

    return () => {
      if (pollId) window.clearInterval(pollId);
    };
  }, [isEnabled, siteId, isDevLike, isProd, ENV]);

  return { snippetReady: Boolean(siteId) && snippetReady };
};

const useCustomerIO = () => {
  const isEnabled = useVeroFlag(VeroFlag.customerioSnippetEnabled);
  const { snippetReady } = useCustomerIOSnippet(isEnabled);
  useCustomerioIdentify({ enabled: isEnabled, snippetReady });
};

export const CustomerIO = () => {
  useCustomerIO();
  return null;
};
